import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useAxleDataUpsertOrgsQuery, useUpsertAxleDataMutation } from "../../../types";
import { useSnackbar } from "../../../components/Snackbar/SnackbarContext";

export const AxleDataUpsertPage = () => {
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState<any>(null);

  const { data: orgsData, loading: orgsLoading } = useAxleDataUpsertOrgsQuery();
  const { setMessage } = useSnackbar();

  const [upsertAxleData, { loading: mutationLoading }] = useUpsertAxleDataMutation({
    onCompleted: data => {
      setResult(data.upsertAxleData);
      setMessage("success", data.upsertAxleData.message);
    },
    onError: e => {
      setMessage("error", e.message);
      setResult(null);
    },
  });

  const handleSubmit = () => {
    if (selectedOrgIds.length === 0) {
      setMessage("error", "Please select at least one organization");
      return;
    }

    if (selectedOrgIds.length > 10) {
      setMessage("error", "Please select no more than 10 organizations");
      return;
    }

    setResult(null);
    upsertAxleData({
      variables: {
        input: {
          dryRun,
          orgIds: selectedOrgIds,
        },
      },
    });
  };

  if (orgsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Typography variant="h4" gutterBottom>
        Axle Data Upsert
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="org-select-label">Organizations</InputLabel>
          <Select
            labelId="org-select-label"
            multiple
            value={selectedOrgIds}
            onChange={e => setSelectedOrgIds(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
            label="Organizations"
            renderValue={selected => `${selected.length} organization${selected.length !== 1 ? "s" : ""} selected`}
          >
            {orgsData?.orgs.map(org => (
              <MenuItem key={org.id} value={org.id}>
                <Checkbox checked={selectedOrgIds.indexOf(org.id) > -1} />
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select up to 10 organizations
        </Typography>

        <FormControlLabel
          control={<Checkbox checked={dryRun} onChange={e => setDryRun(e.target.checked)} />}
          label="Dry Run (preview changes without applying)"
        />

        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={handleSubmit} disabled={mutationLoading || selectedOrgIds.length === 0}>
            {mutationLoading ? <CircularProgress size={24} /> : dryRun ? "Preview Upsert" : "Run Upsert"}
          </Button>
        </Box>
      </Paper>

      {result && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h3" gutterBottom>
            Results
          </Typography>

          <Alert severity={result.success ? "success" : "error"} sx={{ mb: 2 }}>
            {result.message}
          </Alert>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Nutrition Orgs
              </Typography>
              <Typography variant="h3">{result.summary.totalNutritionOrgs}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Already Synced
              </Typography>
              <Typography variant="h3">{result.summary.alreadySynced}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Matched and Synced
              </Typography>
              <Typography variant="h3">{result.summary.matchedAndSynced}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Created New Orgs
              </Typography>
              <Typography variant="h3" sx={{ color: 'success.main' }}>
                {result.summary.created}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                No Matches
              </Typography>
              <Typography variant="h3">{result.summary.noMatches}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Unexpected Partial Matches
              </Typography>
              <Typography variant="h3">{result.summary.unexpectedPartialMatches}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Orgs Processed
              </Typography>
              <Typography variant="h3">{result.summary.orgsProcessed}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Athlete Profiles
              </Typography>
              <Typography variant="h3">{result.summary.totalAthleteProfiles}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Non-Athlete Profiles
              </Typography>
              <Typography variant="h3">{result.summary.totalNonAthleteProfiles}</Typography>
            </Box>

            <Box sx={{ gridColumn: "1 / -1" }}>
              <Typography variant="body2" color="text.secondary">
                Total Profiles
              </Typography>
              <Typography variant="h3" sx={{ fontSize: "2rem" }}>
                {result.summary.totalProfiles}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};
